import {
  ErrorTypes,
  FlattenedErrorsObjectArray,
  Flex,
  FlexGrow,
  InfoContainerUnpadded,
  InfoList,
  InfoListContainer,
  PageRefName,
  useRefs,
  WarningInfo,
  WarningInfoDanger,
  WarningInfoMinorDanger,
} from '@equifinance/shared';
import styled from 'styled-components';
import calculateOffsetTop from '../../utils/calculateOffsetTop';
import { useTabNavHeight } from '../AccountTabNav';

export const InfoListItem = styled(Flex) <{ seperateLine: boolean }>`${({ theme, seperateLine = false }) => `
    gap: ${theme.padding[2]};
    padding: ${theme.padding[2]} 0;
    border-bottom: ${seperateLine ? `1px solid ${theme.colours.neutral[6]}` : 'unset'}
`}`;

export const InfoListItemAction = styled(Flex)`${({ theme }) => `
    border-left: 1px solid ${theme.colours.neutral[6]};
    font-weight: ${theme.fontWeight.bold};
    padding: 0 ${theme.padding[3]};
    align-items: center;
    font-size: ${theme.fontSize[2]};
    gap: ${theme.padding[2]};
    cursor: pointer;
`}`;

const Icon = styled.i`${({ theme }) => `
    color: ${theme.colours.neutral[2]};
    font-size: ${theme.fontSize[3]};
`}`;

export type NavigableInfoListItemType = { errorMessage: string, linkedId?: string };

export function NavigableInfoAction({ linkedId = '' }: { linkedId?: string }) {
  const { refs } = useRefs();
  const stickyHeaderOffset = useTabNavHeight();

  const goToSection = (linkedId: string) => {
    const { current } = refs?.[linkedId] ?? {};
    const offsetTop = calculateOffsetTop(current);

    if (current) {
      refs[PageRefName]?.current.scrollTo({ top: (offsetTop - stickyHeaderOffset), behavior: 'smooth' });
    }
  };

  return (
    (linkedId ? (
      <InfoListItemAction onClick={() => { goToSection(linkedId); }}>
        <Icon className="mi-circle-arrow-right" />
        Go to section
      </InfoListItemAction>
    ) : null
    )
  );
}

export function NavigableInfoList({ items }: { items: NavigableInfoListItemType[] }) {
  return (
    <InfoListContainer itemCount={items.length}>
      {items.map((item, index) => (
        <li>
          <InfoListItem seperateLine={index < items.length - 1}>
            <FlexGrow>
              {item.errorMessage}
            </FlexGrow>

            <NavigableInfoAction linkedId={item.linkedId} />
          </InfoListItem>
        </li>
      ))}
    </InfoListContainer>
  );
}

export default function PageErrors({
  id,
  errors,
  warnings,
}: {
  id: string,
  errors: FlattenedErrorsObjectArray,
  warnings: FlattenedErrorsObjectArray,
}) {
  const rejectPolicyErrors = errors?.filter((fe) => fe.id === id && fe.errorType === ErrorTypes.RejectPolicy);
  const rejectDataErrors = errors?.filter((fe) => fe.id === id && fe.errorType === ErrorTypes.RejectData);
  const referWarnings = warnings?.filter((fe) => fe.id === id && fe.errorType === ErrorTypes.Refer);

  return (
    <InfoContainerUnpadded>
      {rejectPolicyErrors.length > 0 && (
        <WarningInfoMinorDanger>
          <InfoList items={rejectPolicyErrors?.map((fe) => (
            fe.errorMessage
          ))}
          />
        </WarningInfoMinorDanger>
      )}

      {rejectDataErrors?.length > 0 && (
        <WarningInfoDanger>
          <NavigableInfoList items={rejectDataErrors} />
        </WarningInfoDanger>
      )}

      {referWarnings?.length > 0 && (
        <WarningInfo>
          <NavigableInfoList items={referWarnings} />
        </WarningInfo>
      )}

    </InfoContainerUnpadded>
  );
}
